import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import media from "../../../media";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState("dashboard");
  const currentAuditType =
    location.state?.auditType || secureLocalStorage.getItem("auditType");

  const segments = location.pathname.split("/").filter(Boolean);
  const base = segments[0];
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  /* =========================================================
   🔥 GLOBAL TABLE SELECTION RESET (SINGLE SOURCE OF TRUTH)
   ========================================================= */
  useEffect(() => {
    if (
      globalState?.selectedRowKeys?.length ||
      globalState?.selectedRowKeysChildInfo?.length
    ) {
      setGlobalState((prev) => ({
        ...prev,
        selectedRowKeys: [],
        selectedRowKeysChildInfo: [],
      }));
    }

    //CLEAR SEARCH WHEN NAVIGATION CHANGES
    const searchInput = document.querySelector(".inputS");
    if (searchInput) searchInput.value = "";

    secureLocalStorage.removeItem("hasSearchedValue");
    secureLocalStorage.removeItem("searchKeyword");
  }, [location.pathname]);

  const child = segments[1];
  const [isFeedbackRequestsActive, setIsFeedbackRequestsActive] =
    useState(false);

  const [isEscalationsActive, setIsEscalationsActive] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const role = secureLocalStorage.getItem("role");

  const handleFeedbackRequestsClick = () => {
    setIsFeedbackRequestsActive((prev) => !prev); // toggle open/close
  };

  const handleClientEscalationClick = () => {
    setIsEscalationsActive((prev) => !prev); // TOGGLE
  };

  const handleClick = (val) => {
    setIsActive((prev) => (prev === val ? "" : val));
  };

  const handleSubMenuClick = (val, path) => {
    const cleanPath = path?.replace("/", "");
    const lastPageSearchIn = secureLocalStorage.getItem("lastPageSearchIn");
    if (cleanPath && lastPageSearchIn && cleanPath !== lastPageSearchIn) {
      const searchInput = document.querySelector(".inputS");
      if (searchInput) searchInput.value = "";

      secureLocalStorage.removeItem("hasSearchedValue");
      secureLocalStorage.removeItem("searchKeyword");
    }
  };

  useEffect(() => {
    // RESET submenu states first
    setIsFeedbackRequestsActive(false);
    setIsEscalationsActive(false);

    // DASHBOARD
    if (!base || base === "dashboard") {
      setIsActive("dashboard");
      return;
    }

    // QMS (list + detail pages)
    if (
      [
        "auditForms",
        "audits",
        "deleted-audits",
        "processes",
        "clients",
        "departments",
        "escalations",
      ].includes(base)
    ) {
      setIsActive("qms");

      if (base === "escalations") {
        setIsEscalationsActive(true);
        setActiveSubMenu("Escalations");
      }
      if (base === "audits") {
        setActiveSubMenu("Audits");
      }

      return;
    }

    // VOC
    if (["feedback-requests", "forms"].includes(base)) {
      setIsActive("voc");

      if (base === "feedback-requests") {
        setIsFeedbackRequestsActive(true);
        setActiveSubMenu("Feedback Requests");
      }
      return;
    }

    // MANAGE USERS
    if (["users", "roles", "permissions", "sections"].includes(base)) {
      setIsActive("mpsru");
      return;
    }

    // SETTINGS
    if (base === "settings") {
      setIsActive("settings");
    }
  }, [base]);

  const menuConfig = {
    qms: [
      {
        admin: [
          { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
          {
            path: "",
            label: "Audits",
            icon: "Audits.png",
            subLinks: [
              {
                path: "/audits",
                label: "My Audits",
                type: "my",
                icon: "Audits.png",
              },
              {
                path: "/audits",
                label: "Auditor's Audits",
                type: "auditors",
                icon: "Audits.png",
              },
              {
                path: "/audits",
                label: "Supervisor's Audits",
                type: "supervisors",
                icon: "Audits.png",
              },
            ],
          },
          {
            path: "/deleted-audits",
            label: "Deleted Audits",
            icon: "Audits.png",
          },
          {
            path: "",
            label: "Escalations",
            icon: "escalations.png",
            subLinks: [
              {
                path: "/escalations/client-escalation",
                label: "Client Escalation",
                icon: "escalations.png",
              },
              {
                path: "/escalations/internal-error-rca",
                label: "Internal Error RCA",
                icon: "escalations.png",
              },
            ],
          },
          { path: "/processes", label: "Processes", icon: "fa fa-refresh" },
          { path: "/clients", label: "Clients", icon: "fa fa-user-secret" },
          {
            path: "/departments",
            label: "Departments",
            icon: "fa fa-building-o",
          },
        ],
        agent: [{ path: "/audits", label: "Audits", icon: "Audits.png" }],
        atl: [{ path: "/audits", label: "Audits", icon: "Audits.png" }],
        auditor: [
          { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
          { path: "/audits", label: "Audits", icon: "Audits.png" },
          {
            path: "/deleted-audits",
            label: "Deleted Audits",
            icon: "Audits.png",
          },
          {
            path: "",
            label: "Escalations",
            icon: "escalations.png",
            subLinks: [
              {
                path: "/escalations/internal-error-rca",
                label: "Internal Error RCA",
                icon: "escalations.png",
              },
            ],
          },
        ],
        qc: [
          {
            path: "/departments",
            label: "Departments",
            icon: "fa fa-building-o",
          },
          { path: "/processes", label: "Processes", icon: "fa fa-refresh" },
          { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
          {
            path: "",
            label: "Audits",
            icon: "Audits.png",
            subLinks: [
              {
                path: "/audits",
                label: "Auditor's Audits",
                type: "auditors",
                icon: "Audits.png",
              },
              {
                path: "/audits",
                label: "Supervisor's Audits",
                type: "supervisors",
                icon: "Audits.png",
              },
            ],
          },
          {
            path: "/deleted-audits",
            label: "Deleted Audits",
            icon: "Audits.png",
          },
          {
            path: "",
            label: "Escalations",
            icon: "escalations.png",
            subLinks: [
              {
                path: "/escalations/client-escalation",
                label: "Client Escalation",
                icon: "escalations.png",
              },
              {
                path: "/escalations/internal-error-rca",
                label: "Internal Error RCA",
                icon: "escalations.png",
              },
            ],
          },
        ],
        tl: [
          {
            path: "",
            label: "Audits",
            icon: "Audits.png",
            subLinks: [
              {
                path: "/audits",
                label: "My Audits",
                type: "my",
                icon: "Audits.png",
              },
              {
                path: "/audits",
                label: "Auditor's Audits",
                type: "auditors",
                icon: "Audits.png",
              },
            ],
          },
        ],
      },
    ],
    voc: [
      {
        admin: [
          {
            path: "",
            label: "Feedback Requests",
            icon: "fa fa-commenting-o",
            subLinks: [
              {
                path: "/feedback-requests/open",
                label: "Open",
                icon: "fa fa-commenting-o",
              },
              {
                path: "/feedback-requests/closed",
                label: "Closed",
                icon: "fa fa-commenting-o",
              },
              {
                path: "/feedback-requests/expired",
                label: "Expired",
                icon: "fa fa-commenting-o",
              },
            ],
          },
          { path: "/forms/add", label: "Add Form", icon: "fa fa-wpforms" },
          { path: "/forms", label: "Forms", icon: "fa fa-wpforms" },
        ],
        qc: [
          {
            path: "",
            label: "Feedback Requests",
            icon: "fa fa-commenting-o",
            subLinks: [
              {
                path: "/feedback-requests/open",
                label: "Open",
                icon: "fa fa-commenting-o",
              },
              {
                path: "/feedback-requests/closed",
                label: "Closed",
                icon: "fa fa-commenting-o",
              },
              {
                path: "/feedback-requests/expired",
                label: "Expired",
                icon: "fa fa-commenting-o",
              },
            ],
          },
          { path: "/forms", label: "Forms", icon: "fa fa-wpforms" },
        ],
      },
    ],
    mpsru: [
      {
        admin: [
          { path: "/permissions", label: "Permissions", icon: "fa fa-wpforms" },
          { path: "/sections", label: "Sections", icon: "fa fa-wpforms" },
          { path: "/roles", label: "Roles", icon: "fa fa-wpforms" },
          { path: "/users", label: "Users", icon: "fa fa-users" },
        ],
      },
    ],
    settings: [
      {
        admin: [
          {
            path: "/settings/theme-options",
            label: "Theme Options",
            icon: "fa fa-wpforms",
          },
          {
            path: "/settings/import-bulk-data",
            label: "Import Bulk Data",
            icon: "fa fa-wpforms",
          },
        ],
      },
    ],
    switchRole: [
      {
        admin: [
          {
            path: "/switch-role",
            label: "Switch Role",
            icon: "fa fa-wpforms",
          },
        ],
      },
    ],
  };

  const sections = [
    {
      key: "qms",
      title: "QMS",
      icon: media.qmsIcon.svg,
      roles: ["admin", "qc", "auditor", "atl", "agent", "tl"],
    },
    {
      key: "voc",
      title: "VOC",
      icon: media.voc.svg,
      roles: ["admin", "qc"],
    },
    {
      key: "mpsru",
      title: "Manage Users",
      icon: media.manageUsers.svg,
      roles: ["admin"],
    },
    {
      key: "settings",
      title: "Settings",
      icon: media.settingsIcon.svg,
      roles: ["admin"],
    },
    {
      key: "switchRole",
      title: "Switch Role",
      icon: media.switch.svg,
      roles: ["admin"],
      path: "switch-role",
      condition:
        secureLocalStorage.getItem("empID") ===
        Number(process.env.REACT_APP_SROLE_ID),
    },
  ];

  const renderSubLinks = (subLinks) =>
    subLinks.map(({ path, label, type }) => {
      const isActive =
        location.pathname.startsWith(path) &&
        (type ? currentAuditType === type : true);

      return (
        <Link
          key={label}
          to={path}
          state={{ auditType: type }}
          onClick={() => {
            secureLocalStorage.setItem("auditType", type);

            handleSubMenuClick(null, path);

            // CRITICAL FIX
            if (path.startsWith("/escalations")) {
              setIsActive("qms");
              setIsEscalationsActive(true);
              setActiveSubMenu("Escalations");
            }

            if (path.startsWith("/feedback-requests")) {
              setIsActive("voc");
              setIsFeedbackRequestsActive(true);
            }
          }}
          className={`links ${isActive ? "subMenu-active-item" : ""}`}
        >
          <span className="dashboardstyle">{label}</span>
        </Link>
      );
    });

  return (
    <div className="sidenav">
      <div
        className="logo-area"
        onClick={() => {
          navigate("/dashboard");
        }}
      >
        {globalState?.logo && (
          <img
            src={process.env.REACT_APP_BACKEND + `/images/${globalState?.logo}`}
            className="logo-img"
            alt="logo"
          />
        )}
        <span className="qcc_logo">{globalState?.logoTitle}</span>
      </div>
      <div className="menu">
        {/*Dashboard*/}
        <div
          className={
            location.pathname === "/dashboard" || location.pathname === "/"
              ? "menuActive"
              : ""
          }
        >
          <span
            className="d-flex links"
            onClick={() => {
              navigate("/dashboard");
            }}
          >
            <span className="dboardLnkContnt link-content">
              <img src={media.dashboardIcon.svg} alt="icon" />
              Dashboard
            </span>
            <i className={isActive === "dashboard" ? "" : ""} />
          </span>
        </div>

        {sections.map(({ key, title, icon, roles, path, condition }) => {
          if (!roles.includes(role)) return null;
          if (condition === false) return null;

          if (key === "switchRole") {
            const link = menuConfig?.[key]?.[0]?.[role]?.[0];
            if (!link) return null;

            return (
              <div
                key={key}
                className={
                  location.pathname.startsWith(link?.path) ? "menuActive" : ""
                }
              >
                <Link
                  to="/switch-role"
                  className="d-flex links"
                  onClick={() => {
                    handleClick(link?.path?.replace("/", ""));
                    handleSubMenuClick(link?.path?.replace("/", ""));
                  }}
                >
                  <span className="link-content">
                    <img style={{ marginRight: 0 }} src={icon} alt="icon" />
                    {link.label}
                  </span>
                </Link>
              </div>
            );
          }

          const links = menuConfig?.[key]?.[0]?.[role];

          if (!links) return null;

          return (
            <div key={key} className={isActive === key ? "menuActive" : ""}>
              <span className="d-flex links" onClick={() => handleClick(key)}>
                <span className="link-content">
                  <img src={icon} alt="icon" /> {title}
                </span>

                <i
                  className={
                    isActive === key
                      ? "fa fa-angle-up dropdown"
                      : "fa fa-angle-down dropdown"
                  }
                />
              </span>

              <div
                className={
                  isActive === key ? "activeClass" : "inactiveClass dropwdown"
                }
              >
                {links.map(({ path, label, subLinks }) => (
                  <React.Fragment key={label}>
                    {subLinks ? (
                      <>
                        <span
                          className="links subMnu"
                          onClick={() =>
                            setActiveSubMenu((prev) =>
                              prev === label ? null : label,
                            )
                          }
                        >
                          <span className="dashboardstyle">
                            {label}
                            <i
                              className={`fa dropdown ${
                                activeSubMenu === label
                                  ? "fa-angle-up"
                                  : "fa-angle-down"
                              }`}
                            />
                          </span>
                        </span>

                        <div
                          className={
                            activeSubMenu === label
                              ? "activeClass subMnuChildActive"
                              : "inactiveClass dropwdown"
                          }
                        >
                          {renderSubLinks(subLinks)}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={path}
                        className={`links ${
                          location.pathname === path ||
                          (path !== "/forms" &&
                            location.pathname.startsWith(path))
                            ? "subMenu-active-item"
                            : ""
                        }`}
                        onClick={() => handleSubMenuClick(key, path)}
                      >
                        <span className="dashboardstyle">{label}</span>
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NavBar;
